import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { Inbox as InboxIcon, Send, UserCheck, CheckCircle2, MessageSquare, Zap, Plus, X } from 'lucide-react'
import { getSession } from '../lib/session'
import AgentAsk from '../components/AgentAsk'

// Shared team inbox for two-way WhatsApp. Left: the conversation list with
// assignee/status filters. Right: the selected thread + a reply box with
// one-tap canned responses. Mirrors the campaigns surface's tenant-scoped fetches.
const STATUS_CLS = {
  open:     'bg-green-500/10 text-green-600',
  pending:  'bg-amber-500/10 text-amber-600',
  closed:   'bg-gray-100 text-gray-400',
}

export default function Inbox() {
  const session = getSession()
  const [conversations, setConversations] = useState([])
  const [loading, setLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState('open')
  const [assigneeFilter, setAssigneeFilter] = useState('')
  const [selectedId, setSelectedId] = useState(null)
  const [thread, setThread] = useState(null)
  const [messages, setMessages] = useState([])
  const [reply, setReply] = useState('')
  const [sending, setSending] = useState(false)
  const [canned, setCanned] = useState([])
  const [showCanned, setShowCanned] = useState(false)
  const endRef = useRef(null)

  const loadConversations = () => {
    if (!session?.namespaceId) return
    setLoading(true)
    const params = {}
    if (statusFilter) params.status = statusFilter
    if (assigneeFilter.trim()) params.assigned_to = assigneeFilter.trim()
    axios.get('/api/v1/inbox', { params })
      .then(r => {
        const list = r.data.conversations || r.data.threads || (Array.isArray(r.data) ? r.data : [])
        setConversations(list)
        setSelectedId(prev => prev || list[0]?.id || null)
      })
      .catch(err => toast.error(err.response?.data?.error || 'Failed to load inbox'))
      .finally(() => setLoading(false))
  }
  useEffect(loadConversations, [session?.namespaceId, statusFilter, assigneeFilter])

  useEffect(() => {
    axios.get('/api/v1/inbox/canned').then(r => setCanned(r.data.canned || r.data.replies || (Array.isArray(r.data) ? r.data : []))).catch(() => {})
  }, [session?.namespaceId])

  const loadThread = (id) => {
    if (!id) { setThread(null); setMessages([]); return }
    axios.get(`/api/v1/inbox/${id}`)
      .then(r => {
        setThread(r.data.conversation || r.data.thread || r.data)
        setMessages(r.data.messages || r.data.conversation?.messages || [])
      })
      .catch(err => toast.error(err.response?.data?.error || 'Failed to load conversation'))
  }
  useEffect(() => loadThread(selectedId), [selectedId])
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])

  const send = async (text) => {
    const body = (text ?? reply).trim()
    if (!body || !selectedId) return
    setSending(true)
    try {
      const r = await axios.post(`/api/v1/inbox/${selectedId}/reply`, { text: body })
      const msg = r.data.message || r.data
      setMessages(m => [...m, msg?.id ? msg : { id: `local-${Date.now()}`, direction: 'outbound', text: body, created_at: new Date().toISOString() }])
      setReply('')
    } catch (err) { toast.error(err.response?.data?.error || 'Could not send reply') }
    finally { setSending(false) }
  }

  const assign = async () => {
    const who = window.prompt('Assign to (email or name)', thread?.assigned_to || '')
    if (who === null) return
    try {
      const r = await axios.patch(`/api/v1/inbox/${selectedId}/assign`, { assigned_to: who.trim() || null })
      const next = r.data.conversation || r.data
      setThread(t => ({ ...t, assigned_to: next.assigned_to ?? (who.trim() || null) }))
      setConversations(list => list.map(c => c.id === selectedId ? { ...c, assigned_to: who.trim() || null } : c))
      toast.success(who.trim() ? `Assigned to ${who.trim()}` : 'Unassigned')
    } catch (err) { toast.error(err.response?.data?.error || 'Could not assign') }
  }

  const setStatus = async (status) => {
    try {
      await axios.patch(`/api/v1/inbox/${selectedId}/status`, { status })
      setThread(t => ({ ...t, status }))
      setConversations(list => list.map(c => c.id === selectedId ? { ...c, status } : c))
      toast.success(status === 'closed' ? 'Conversation closed' : `Marked ${status}`)
      if (statusFilter && status !== statusFilter) loadConversations()
    } catch (err) { toast.error(err.response?.data?.error || 'Could not update status') }
  }

  const name = (c) => c.name || c.contact_name || `+${c.wa_id || c.contact_wa_id || ''}`

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><InboxIcon size={24} className="text-primary-600" /> Inbox</h2>
          <p className="text-gray-500 mt-1">Your team's shared WhatsApp conversations.</p>
        </div>
      </div>

      <div className="max-w-2xl"><AgentAsk service="inbox" agentName="Inbox Assist Agent" starters={['How many open conversations?', 'Draft a reply to the latest message']} /></div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {['open', 'pending', 'closed', ''].map(s => (
          <button key={s || 'all'} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm capitalize border ${statusFilter === s ? 'border-primary-600 text-primary-600 bg-primary-500/5' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            {s || 'All'}
          </button>
        ))}
        <input value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)} placeholder="Filter by assignee…"
          className="input w-auto ml-auto max-w-[220px]" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4 h-[calc(100vh-260px)] min-h-[480px]">
        {/* Conversation list */}
        <div className="card p-0 overflow-auto">
          {loading && <p className="p-4 text-center text-gray-400 text-sm">Loading…</p>}
          {!loading && conversations.length === 0 && <p className="p-6 text-center text-gray-400 text-sm">No conversations here.</p>}
          <ul className="divide-y divide-gray-100">
            {conversations.map(c => (
              <li key={c.id}>
                <button onClick={() => setSelectedId(c.id)}
                  className={`w-full text-left px-4 py-3 transition-colors ${selectedId === c.id ? 'bg-primary-500/5' : 'hover:bg-gray-50'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm text-gray-900 truncate">{name(c)}</span>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full capitalize shrink-0 ${STATUS_CLS[c.status] || 'bg-gray-100 text-gray-500'}`}>{c.status || 'open'}</span>
                  </div>
                  <p className="text-xs text-gray-400 truncate mt-0.5">{c.last_message || c.preview || c.last_message_text || '—'}</p>
                  {c.assigned_to && <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1"><UserCheck size={11} /> {c.assigned_to}</p>}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Thread */}
        <div className="card p-0 flex flex-col overflow-hidden">
          {!thread ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
              <div className="text-center"><MessageSquare size={28} className="mx-auto mb-2 text-gray-300" />Select a conversation</div>
            </div>
          ) : (
            <>
              {/* Thread header */}
              <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{name(thread)}</p>
                  <p className="text-xs text-gray-400">{thread.assigned_to ? `Assigned to ${thread.assigned_to}` : 'Unassigned'}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={assign} className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1"><UserCheck size={15} /> Assign</button>
                  {thread.status !== 'closed'
                    ? <button onClick={() => setStatus('closed')} className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1"><CheckCircle2 size={15} /> Close</button>
                    : <button onClick={() => setStatus('open')} className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1">Reopen</button>}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-auto p-4 space-y-3 bg-gray-50">
                {messages.length === 0 && <p className="text-center text-gray-400 text-sm">No messages yet.</p>}
                {messages.map(m => {
                  const out = m.direction === 'outbound' || m.from === 'agent' || m.outbound
                  return (
                    <div key={m.id} className={`flex ${out ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${out ? 'bg-primary-600 text-white rounded-br-sm' : 'bg-gray-100 text-gray-800 rounded-bl-sm border border-gray-200'}`}>
                        <p className="whitespace-pre-wrap break-words">{m.text || m.body}</p>
                        <p className={`text-[10px] mt-1 ${out ? 'text-white/70' : 'text-gray-400'}`}>{m.created_at ? new Date(m.created_at).toLocaleString() : ''}</p>
                      </div>
                    </div>
                  )
                })}
                <div ref={endRef} />
              </div>

              {/* Reply box */}
              <div className="border-t border-gray-200 p-3">
                <div className="flex items-end gap-2">
                  <button onClick={() => setShowCanned(true)} title="Canned replies"
                    className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 shrink-0"><Zap size={18} /></button>
                  <textarea value={reply} onChange={e => setReply(e.target.value)} rows={1}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
                    placeholder="Type a reply… (Enter to send, Shift+Enter for newline)"
                    className="input resize-none flex-1" />
                  <button onClick={() => send()} disabled={sending || !reply.trim()}
                    className="btn-primary text-sm flex items-center gap-1 shrink-0 disabled:opacity-60"><Send size={16} /> Send</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {showCanned && (
        <CannedModal canned={canned} onClose={() => setShowCanned(false)}
          onPick={t => { setReply(r => (r ? r + ' ' : '') + t); setShowCanned(false) }}
          onCreated={c => setCanned(list => [...list, c])} />
      )}
    </div>
  )
}

function CannedModal({ canned, onClose, onPick, onCreated }) {
  const [title, setTitle] = useState('')
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)

  const create = async () => {
    if (!text.trim()) { toast.error('Reply text is required'); return }
    setBusy(true)
    try {
      const r = await axios.post('/api/v1/inbox/canned', { title: title.trim() || undefined, text: text.trim() })
      onCreated(r.data.canned || r.data)
      setTitle(''); setText('')
      toast.success('Canned reply saved')
    } catch (err) { toast.error(err.response?.data?.error || 'Could not save') }
    finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-gray-100 border border-gray-200 rounded-xl w-full max-w-md p-6 max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Zap size={18} className="text-primary-600" /> Canned replies</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="space-y-2 mb-4">
          {canned.length === 0 && <p className="text-sm text-gray-400">No saved replies yet — add one below.</p>}
          {canned.map((c, i) => (
            <button key={c.id || i} onClick={() => onPick(c.text || c.body)}
              className="w-full text-left rounded-lg border border-gray-200 hover:bg-gray-50 p-3">
              {c.title && <p className="text-xs font-medium text-gray-500 mb-0.5">{c.title}</p>}
              <p className="text-sm text-gray-800 line-clamp-2">{c.text || c.body}</p>
            </button>
          ))}
        </div>
        <div className="border-t border-gray-200 pt-4 space-y-2">
          <p className="text-xs font-medium text-gray-500 flex items-center gap-1"><Plus size={13} /> New canned reply</p>
          <input className="input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Title (optional)" />
          <textarea className="input" rows={3} value={text} onChange={e => setText(e.target.value)} placeholder="Reply text…" />
          <div className="flex justify-end">
            <button onClick={create} disabled={busy} className="btn-primary text-sm disabled:opacity-60">{busy ? 'Saving…' : 'Save reply'}</button>
          </div>
        </div>
      </div>
    </div>
  )
}
